import { buildMapData, getUniqueMonths } from '@resistapp/client/data-utils/plot-data/build-map-data';
import { buildOverviewLineData, OverviewDatum } from '@resistapp/client/data-utils/plot-data/build-overview-line-data';
import { PoolingMode, PoolingType } from '@resistapp/common/api-types';
import {
  getDefaulProcessModeForMetricAndSelectedSamplingPoints,
  SmplPntGroup,
} from '@resistapp/common/comparable-sample-groups';
import { FullSample, MetricMode, NormalisationMode, ProcessMode } from '@resistapp/common/types';
import { flattenSamplesByUID } from '@resistapp/common/utils';
import { chain, Dictionary, keys, mapValues } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAssayContext } from '../../assay-context';
import { useSampleDataContext } from '../../sample-data-context';
import { shouldEnableProcessGroupLevel } from '../../../utils/process-group-level';
import {
  determineAvailableNormalisationModes,
  getGlobalOverviewDatum,
  shouldSuppressVolumeModesForProject,
} from '../overview-context-utils';

interface UseOverviewDataProps {
  supportedSamples: FullSample[] | undefined;
  queryParamsInitialised: boolean;
  activeSmplPntGroup: SmplPntGroup | undefined;
  metricMode: MetricMode;
}

interface UseOverviewDataResult {
  trendDataByLevel: Dictionary<OverviewDatum[][]> | undefined;
  mapDataByLevel: Dictionary<OverviewDatum[]> | undefined;
  processGroupMapData: OverviewDatum[] | undefined;
  availableMonths: string[];
  notAvailableReason: string | null;
  availableNormalisationModes: NormalisationMode[];
  defaultProcessMode: ProcessMode;
  processGroupLevelEnabled: boolean;
}

/*
 * Encapsulates main data preparation logic for overview use context.
 * NOTE: Keep this hook totally reliant on external state changing in overview-context and shouldn't activate when
 * overview-contexts internal states are updated.
 *
 * DO NOT USE ELSEWHERE THAN ONCE IN use-overview-context.tsx
 */
export function useOverviewData({
  supportedSamples,
  queryParamsInitialised,
  activeSmplPntGroup: activeSmplPntGroup,
  metricMode,
}: UseOverviewDataProps): UseOverviewDataResult {
  const { data, queryFilters, availableNormalisationModes, setAvailableNormalisationModesStable } =
    useSampleDataContext();
  const { assaysLoaded, getGroup } = useAssayContext();

  const mapDataUpdateInvocationCnt = useRef(0);
  const [trendDataByLevel, setTrendDataByLevel] = useState<Dictionary<OverviewDatum[][]> | undefined>(undefined);
  const [mapDataByLevel, setMapDataByLevel] = useState<Dictionary<OverviewDatum[]> | undefined>(undefined);
  const [processGroupMapData, setProcessGroupMapData] = useState<OverviewDatum[] | undefined>(undefined);
  const [notAvailableReason, setNotAvailableReason] = useState<string | null>(null);
  const allVisibleSamples = useMemo(() => (data ? flattenSamplesByUID(data.samplesByUID) : undefined), [data]);
  const focusedSamples = useMemo(
    () => (data?.focusedByUID ? flattenSamplesByUID(data.focusedByUID) : undefined),
    [data?.focusedByUID],
  );
  const forceSuppressVolumeModes = useMemo(
    () => shouldSuppressVolumeModesForProject(allVisibleSamples),
    [allVisibleSamples],
  );
  const processGroupLevelEnabled = useMemo(
    () => shouldEnableProcessGroupLevel(data?.processes, allVisibleSamples),
    [data?.processes, allVisibleSamples],
  );

  // 3.1 RE-DETERMINE AVAILABLE NORMALISATION MODES BASED ON METADATA AVAILABILITY IN SUPPORTEDSAMPLES
  useEffect(() => {
    const supportedNormalisationModes = determineAvailableNormalisationModes(
      supportedSamples,
      activeSmplPntGroup?.smplPnts.map(e => e.id),
      { forceSuppressVolumeModes },
    );
    setAvailableNormalisationModesStable(supportedNormalisationModes);
    return () => {
      // Reset overview specific customisation on unload
      // NOTE: keep consistent with useSampleDataContext
      const modes = determineAvailableNormalisationModes(focusedSamples, undefined, {
        forceSuppressVolumeModes,
      });
      setAvailableNormalisationModesStable(modes);
    };
  }, [supportedSamples, activeSmplPntGroup?.smplPnts, focusedSamples, forceSuppressVolumeModes]);
  const defaultProcessMode = getDefaulProcessModeForMetricAndSelectedSamplingPoints(
    activeSmplPntGroup?.type,
    metricMode,
    supportedSamples || [],
  );

  // 3.2 PREPARE OVERVIEW DATA
  useEffect(() => {
    if (!supportedSamples?.length || !queryParamsInitialised || !activeSmplPntGroup || !assaysLoaded) {
      return;
    }

    // Avoid race conditions that can easily happen eg when an original map data update with all project data is missing co-ordinates,
    // but a subsequent, map update with filtered data isn't
    const thisInvocation = ++mapDataUpdateInvocationCnt.current;
    (() => {
      try {
        const levelsInAllSamples = keys(supportedSamples[0].samplingPoint.adminLevels);
        const adminLevelPoolings: Array<{
          type: PoolingType.SITE_AND_ADMIN_LEVEL;
          mode: PoolingMode.THROW_MISSING;
          level: number;
        }> = levelsInAllSamples.map(level => ({
          type: PoolingType.SITE_AND_ADMIN_LEVEL,
          mode: PoolingMode.THROW_MISSING,
          level: +level,
        }));

        const selectedSmplPntSet = new Set(activeSmplPntGroup.smplPnts.map(smplPnt => smplPnt.id));
        const adminLevelTrendDataByLevel = chain(adminLevelPoolings)
          .keyBy(pooling => `${pooling.level}`)
          // Replace groupYearly with false to always use monthly data
          .mapValues(pooling =>
            buildOverviewLineData(
              supportedSamples,
              selectedSmplPntSet,
              defaultProcessMode,
              pooling,
              getGroup,
              data?.processes ?? [],
            ),
          )
          .value();
        const processGroupTrendData = buildOverviewLineData(
          supportedSamples,
          selectedSmplPntSet,
          defaultProcessMode,
          {
            type: PoolingType.SITE,
            mode: PoolingMode.THROW_MISSING,
          },
          getGroup,
          data?.processes ?? [],
        );
        const stepTrendData = buildOverviewLineData(
          supportedSamples,
          selectedSmplPntSet,
          defaultProcessMode,
          undefined,
          getGroup,
          data?.processes ?? [],
        );
        const useStepTrendDataForNullLevel = processGroupLevelEnabled && metricMode !== MetricMode.REDUCTION;
        const newTrendDataByLevel = {
          ...adminLevelTrendDataByLevel,
          null: useStepTrendDataForNullLevel ? stepTrendData : processGroupTrendData,
        };
        const newProcessGroupMapData = buildMapData(processGroupTrendData, queryFilters.filters.interval);

        const newMapDataByLevel: Dictionary<OverviewDatum[]> = mapValues(newTrendDataByLevel, levelTrendData =>
          buildMapData(levelTrendData, queryFilters.filters.interval),
        );
        newMapDataByLevel['1'] = [getGlobalOverviewDatum()];

        if (mapDataUpdateInvocationCnt.current === thisInvocation) {
          setTrendDataByLevel(newTrendDataByLevel);
          setMapDataByLevel(newMapDataByLevel);
          setProcessGroupMapData(newProcessGroupMapData);
          setNotAvailableReason(null);
        }
      } catch (err) {
        if (err instanceof Error) {
          setNotAvailableReason(err.message);
          console.error('OverviewContext error', err);
        } else {
          console.error('OverviewContext error', err);
        }
      }
    })();
  }, [
    getGroup,
    assaysLoaded,
    supportedSamples,
    queryParamsInitialised, // ^- Wait until these are ready
    queryFilters.filters.interval, // v- rerun after loading and when these change
    activeSmplPntGroup,
    defaultProcessMode,
    metricMode,
    processGroupLevelEnabled,
    data?.processes,
  ]);

  const availableMonths = useMemo(() => {
    if (!trendDataByLevel) {
      return [];
    }
    return getUniqueMonths(trendDataByLevel['null']);
  }, [trendDataByLevel]);

  return {
    trendDataByLevel,
    mapDataByLevel,
    processGroupMapData,
    availableMonths,
    notAvailableReason,
    availableNormalisationModes,
    defaultProcessMode,
    processGroupLevelEnabled,
  };
}
